import { find, forEach, map, filter, isEmpty, pull, get } from 'lodash';

/**
 * Given a string, obtains an array of its bigrams.
 *
 * @param {string} text - The string.
 * @returns {Array} The array of bigrams.
 */
const getBigrams = text => {
  const bigrams = [];
  for (let i = 0; i < text.length - 1; i += 1) {
    const bigram = text.substr(i, 2);
    bigrams.push(bigram);
  }

  return bigrams;
};

/**
 * Given two strings, obtains the Sorensen-Dice similarity.
 *
 * @param {object} arg - The arguments for the function.
 * @param {string} arg.geojsonValue - One string.
 * @param {string} arg.thematicValue - The other string.
 * @returns {number} The Sorensen-Dice similarity.
 */
const getSorensenValue = ({ geojsonValue, thematicValue }) => {
  const geojsonBigrams = getBigrams(geojsonValue);
  const thematicBigrams = getBigrams(thematicValue);
  let matchingPairs = 0;
  const maxScore = geojsonBigrams.length - 1 + thematicBigrams.length - 1;
  forEach(geojsonBigrams, geojsonBigram => {
    const match = find(thematicBigrams, thematicBigram => thematicBigram === geojsonBigram);
    if (match) {
      pull(thematicBigrams, match);
      matchingPairs += 2;
    }
  });
  return matchingPairs / maxScore;
};

/**
 * Given a string and a column, finds in a table the most similar value.
 *
 * @param {object} arg - The arguments for the function.
 * @param {Array} arg.thematicTable - The thematic table to use in the join.
 * @param {string} arg.indexOfHeader - The index of the thematic key to join.
 * @param {string} arg.geojsonValue - The value of the geojson property to join.
 * @param {number} arg.sorensenDiceMinimun - The minimun value for the Sorensen-Dice to join.
 * @returns {object} The row to join and the Sorensen-Dice Score.
 */
export const getThematicRowBySorensenDice = ({
  thematicTable,
  indexOfHeader,
  geojsonValue,
  sorensenDiceMinimun
}) => {
  const thematicCol = filter(
    map(thematicTable, auxRow => auxRow[indexOfHeader]),
    element => !isEmpty(element)
  );
  let scoreObject = { thematicValue: '', value: 0 };
  forEach(thematicCol, thematicValue => {
    const sorensenValue = getSorensenValue({
      geojsonValue,
      thematicValue
    });
    if (sorensenValue > scoreObject.value) scoreObject = { thematicValue, value: sorensenValue };
  });
  return {
    thematicRow:
      scoreObject.value >= sorensenDiceMinimun
        ? find(thematicTable, auxRow => auxRow[indexOfHeader] === scoreObject.thematicValue)
        : undefined,
    score: scoreObject.value >= sorensenDiceMinimun ? scoreObject.value : ''
  };
};

/**
 * Given a string and the name of the property, finds in a geojson the feaature with the most similar value.
 *
 * @param {object} arg - The arguments for the function.
 * @param {Array} arg.geojson - The geojson to use in the join.
 * @param {string} arg.geojsonPropToJoin - The name of the property of the GeoJSON.
 * @param {string} arg.thematicValue - The value of the key element of the Thematic.
 * @param {number} arg.sorensenDiceMinimun - The minimun value for the Sorensen-Dice to join.
 * @returns {object} The row to join and the Sorensen-Dice Score.
 */
export const getGeoJSONFeatureBySorensenDice = ({
  geojson,
  geojsonPropToJoin,
  thematicValue,
  sorensenDiceMinimun
}) => {
  const geojsonArrayValue = filter(
    map(geojson.features, feature => get(feature, ['properties', geojsonPropToJoin])),
    element => !isEmpty(element)
  );
  let scoreObject = { geojsonValue: '', value: 0 };
  forEach(geojsonArrayValue, geojsonValue => {
    const sorensenValue = getSorensenValue({
      geojsonValue,
      thematicValue,
      prevValue: scoreObject.value
    });
    if (scoreObject.value < sorensenValue) {
      scoreObject = { geojsonValue, value: sorensenValue };
    }
  });
  if (scoreObject.value >= sorensenDiceMinimun) {
    return {
      geojsonFeature: find(
        geojson.features,
        feature => get(feature, ['properties', geojsonPropToJoin]) === scoreObject.geojsonValue
      ),
      score: scoreObject.value
    };
  }
  return { geojsonFeature: undefined, score: '' };
};
